/**
 * Marketing module — redesigned (iter 10) with sub-tabs.
 *
 * Sub-tabs:
 *  01 Objetivos — target CPL/CAC metrics with rationale.
 *  02 B2B — channel/budget actions for business clients.
 *  03 B2C — channel/budget actions for end consumers.
 *  04 Digital — paid campaigns with CPL/CAC auto-calc + totals.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Trash2,
  Target,
  Building2,
  ShoppingBag,
  LineChart,
} from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import {
  SectionCard,
  FieldLabel,
  TextInput,
  PrimaryButton,
  SecondaryButton,
  AIBadge,
  Overline,
  SubTabs,
} from "@/components/ui-kit";
import AIAssistant from "@/components/AIAssistant";

const empty = { b2b_actions: [], b2c_actions: [], digital_campaigns: [], target_cpl: 0, target_cac: 0 };

export default function MarketingModule({ data, project, onSave }) {
  const { t, lang } = useLang();
  const [v, setV] = useState({ ...empty, ...(data || {}) });
  const [tab, setTab] = useState("targets");
  useEffect(() => {
    setV((prev) => {
    const next = { ...empty, ...(data || {}) };
    try { if (JSON.stringify(prev) === JSON.stringify(next)) return prev; } catch (e) { void e; }
    return next;
  });
  }, [data]);
  useEffect(() => {
    onSave?.(v);
  }, [v, onSave]);

  const set = (k, val) => setV((p) => ({ ...p, [k]: val }));
  const addAction = (key) =>
    setV((p) => ({ ...p, [key]: [...(p[key] || []), { channel: "", description: "", budget: 0 }] }));
  const updateAction = (key, i, k, val) =>
    setV((p) => ({ ...p, [key]: p[key].map((x, idx) => (idx === i ? { ...x, [k]: val } : x)) }));
  const removeAction = (key, i) =>
    setV((p) => ({ ...p, [key]: p[key].filter((_, idx) => idx !== i) }));

  const addCampaign = () =>
    setV((p) => ({ ...p, digital_campaigns: [...(p.digital_campaigns || []), { platform: "", budget: 0, leads: 0, conversions: 0 }] }));
  const updateCampaign = (i, k, val) =>
    setV((p) => ({ ...p, digital_campaigns: p.digital_campaigns.map((x, idx) => (idx === i ? { ...x, [k]: val } : x)) }));
  const removeCampaign = (i) =>
    setV((p) => ({ ...p, digital_campaigns: p.digital_campaigns.filter((_, idx) => idx !== i) }));

  const totals = useMemo(() => {
    const camps = v.digital_campaigns || [];
    const totalBudget = camps.reduce((s, c) => s + (parseFloat(c.budget) || 0), 0);
    const totalLeads = camps.reduce((s, c) => s + (parseFloat(c.leads) || 0), 0);
    const totalConv = camps.reduce((s, c) => s + (parseFloat(c.conversions) || 0), 0);
    const avgCpl = totalLeads > 0 ? totalBudget / totalLeads : 0;
    const avgCac = totalConv > 0 ? totalBudget / totalConv : 0;
    return { totalBudget, totalLeads, totalConv, avgCpl, avgCac };
  }, [v.digital_campaigns]);

  const b2bBudget = (v.b2b_actions || []).reduce((s, a) => s + (parseFloat(a.budget) || 0), 0);
  const b2cBudget = (v.b2c_actions || []).reduce((s, a) => s + (parseFloat(a.budget) || 0), 0);

  const suggestions = lang === "es"
    ? [
        "Sugiere 5 acciones B2B con presupuestos estimados",
        "Diseña una estrategia B2C de contenidos + ads",
        "Propón distribución de presupuesto entre Google, Meta y otros",
        "Estima CPL y CAC realistas para este negocio",
      ]
    : [
        "Suggest 5 B2B actions with estimated budgets",
        "Design a B2C content + ads strategy",
        "Propose budget distribution across Google, Meta and others",
        "Estimate realistic CPL and CAC",
      ];

  const tabs = [
    { key: "targets", label: lang === "es" ? "Objetivos" : "Targets",     icon: Target },
    { key: "b2b",     label: "B2B",                                       icon: Building2 },
    { key: "b2c",     label: "B2C",                                       icon: ShoppingBag },
    { key: "digital", label: lang === "es" ? "Campañas digitales" : "Digital campaigns", icon: LineChart },
  ];

  return (
    <>
      <SectionCard
        title={t("module.marketing")}
        subtitle={lang === "es" ? "Objetivos, acciones B2B/B2C y campañas digitales." : "Targets, B2B/B2C actions and digital campaigns."}
        action={<div className="flex items-center gap-3"><AIBadge /><AIAssistant module="marketing" context={{ project: project?.name }} suggestions={suggestions} /></div>}
        testId="marketing-section"
      >
        <SubTabs tabs={tabs} active={tab} onChange={setTab} testIdPrefix="marketing-subtab" />

        {tab === "targets" && (
          <div data-testid="marketing-tab-targets" className="space-y-5">
            <p className="text-sm text-[#5C5B5A] max-w-2xl">
              {lang === "es"
                ? "Define los objetivos de adquisición. Estas métricas guiarán las decisiones del resto de pestañas."
                : "Define acquisition targets. These metrics guide decisions in the other tabs."}
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="border border-[#E8E6E1] rounded-sm p-5">
                <FieldLabel hint={lang === "es" ? "Coste por Lead" : "Cost per Lead"}>{t("marketing.target_cpl")}</FieldLabel>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-[#8A8886]">€</span>
                  <TextInput type="number" value={v.target_cpl} onChange={(x) => set("target_cpl", parseFloat(x) || 0)} testId="marketing-target-cpl-input" />
                </div>
              </div>
              <div className="border border-[#E8E6E1] rounded-sm p-5">
                <FieldLabel hint={lang === "es" ? "Coste de Adquisición de Cliente" : "Customer Acquisition Cost"}>{t("marketing.target_cac")}</FieldLabel>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-[#8A8886]">€</span>
                  <TextInput type="number" value={v.target_cac} onChange={(x) => set("target_cac", parseFloat(x) || 0)} testId="marketing-target-cac-input" />
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "b2b" && (
          <div data-testid="marketing-tab-b2b" className="space-y-5">
            <div className="flex items-center justify-between">
              <Overline>{t("marketing.b2b")}</Overline>
              <SecondaryButton onClick={() => addAction("b2b_actions")} testId="b2b-add-btn">
                <Plus className="h-4 w-4" strokeWidth={1.5} />
                {t("marketing.add_action")}
              </SecondaryButton>
            </div>
            <ActionList
              actions={v.b2b_actions || []}
              totalBudget={b2bBudget}
              testIdPrefix="b2b"
              lang={lang}
              t={t}
              onUpdate={(i, field, val) => updateAction("b2b_actions", i, field, val)}
              onRemove={(i) => removeAction("b2b_actions", i)}
            />
          </div>
        )}

        {tab === "b2c" && (
          <div data-testid="marketing-tab-b2c" className="space-y-5">
            <div className="flex items-center justify-between">
              <Overline>{t("marketing.b2c")}</Overline>
              <SecondaryButton onClick={() => addAction("b2c_actions")} testId="b2c-add-btn">
                <Plus className="h-4 w-4" strokeWidth={1.5} />
                {t("marketing.add_action")}
              </SecondaryButton>
            </div>
            <ActionList
              actions={v.b2c_actions || []}
              totalBudget={b2cBudget}
              testIdPrefix="b2c"
              lang={lang}
              t={t}
              onUpdate={(i, field, val) => updateAction("b2c_actions", i, field, val)}
              onRemove={(i) => removeAction("b2c_actions", i)}
            />
          </div>
        )}

        {tab === "digital" && (
          <div data-testid="marketing-tab-digital" className="space-y-5">
            <div className="flex items-center justify-between">
              <Overline>{t("marketing.digital")}</Overline>
              <SecondaryButton onClick={addCampaign} testId="add-campaign-btn">
                <Plus className="h-4 w-4" strokeWidth={1.5} />
                {t("marketing.add_campaign")}
              </SecondaryButton>
            </div>
            {(v.digital_campaigns || []).length === 0 ? (
              <div className="text-sm text-[#8A8886] py-6 text-center border border-dashed border-[#E8E6E1] rounded-sm">
                {lang === "es" ? "Sin campañas. Añade la primera." : "No campaigns yet. Add the first one."}
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {v.digital_campaigns.map((c, i) => {
                    const cpl = c.leads > 0 ? c.budget / c.leads : 0;
                    const cac = c.conversions > 0 ? c.budget / c.conversions : 0;
                    return (
                      <div key={i} className="border border-[#E8E6E1] rounded-sm p-4 bg-white" data-testid={`campaign-${i}`}>
                        <div className="grid md:grid-cols-5 gap-3">
                          <div>
                            <FieldLabel>{t("marketing.platform")}</FieldLabel>
                            <TextInput value={c.platform} onChange={(x) => updateCampaign(i, "platform", x)} placeholder="Google, Meta..." testId={`campaign-${i}-platform`} />
                          </div>
                          <div>
                            <FieldLabel>{t("marketing.budget")}</FieldLabel>
                            <TextInput type="number" value={c.budget} onChange={(x) => updateCampaign(i, "budget", parseFloat(x) || 0)} testId={`campaign-${i}-budget`} />
                          </div>
                          <div>
                            <FieldLabel>{t("marketing.leads")}</FieldLabel>
                            <TextInput type="number" value={c.leads} onChange={(x) => updateCampaign(i, "leads", parseFloat(x) || 0)} testId={`campaign-${i}-leads`} />
                          </div>
                          <div>
                            <FieldLabel>{t("marketing.conversions")}</FieldLabel>
                            <TextInput type="number" value={c.conversions} onChange={(x) => updateCampaign(i, "conversions", parseFloat(x) || 0)} testId={`campaign-${i}-conv`} />
                          </div>
                          <div className="flex items-end justify-between gap-2">
                            <div className="font-mono text-xs text-[#5C5B5A]" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                              <div>CPL: €{cpl.toFixed(2)}</div>
                              <div>CAC: €{cac.toFixed(2)}</div>
                            </div>
                            <button onClick={() => removeCampaign(i)} className="text-[#8A8886] hover:text-[#9E2A2B]" data-testid={`campaign-${i}-remove`}>
                              <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="border-t border-[#E8E6E1] pt-4 grid grid-cols-2 md:grid-cols-4 gap-4 font-mono text-sm" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                  <div>
                    <Overline>{lang === "es" ? "Inversión" : "Spend"}</Overline>
                    <div className="text-lg text-[#1A1A1A] mt-1">€{totals.totalBudget.toLocaleString()}</div>
                  </div>
                  <div>
                    <Overline>Leads</Overline>
                    <div className="text-lg text-[#1A1A1A] mt-1">{totals.totalLeads.toLocaleString()}</div>
                  </div>
                  <div>
                    <Overline>{t("marketing.cpl")}</Overline>
                    <div className="text-lg text-[#B89154] mt-1">€{totals.avgCpl.toFixed(2)}</div>
                  </div>
                  <div>
                    <Overline>{t("marketing.cac")}</Overline>
                    <div className="text-lg text-[#B89154] mt-1">€{totals.avgCac.toFixed(2)}</div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </SectionCard>

    </>
  );
}


function ActionList({ actions, totalBudget, testIdPrefix, lang, t, onUpdate, onRemove }) {
  if (actions.length === 0) {
    return (
      <div className="text-sm text-[#8A8886] py-6 text-center border border-dashed border-[#E8E6E1] rounded-sm">
        {lang === "es" ? "Sin acciones. Añade la primera." : "No actions yet. Add the first one."}
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {actions.map((a, i) => (
        <div
          key={i}
          className="grid md:grid-cols-[1fr_2fr_140px_auto] gap-3 items-end border border-[#E8E6E1] rounded-sm p-4 bg-white"
          data-testid={`${testIdPrefix}-row-${i}`}
        >
          <div>
            <FieldLabel>{t("marketing.channel")}</FieldLabel>
            <TextInput value={a.channel} onChange={(x) => onUpdate(i, "channel", x)} testId={`${testIdPrefix}-${i}-channel`} />
          </div>
          <div>
            <FieldLabel>{lang === "es" ? "Descripción" : "Description"}</FieldLabel>
            <TextInput value={a.description} onChange={(x) => onUpdate(i, "description", x)} testId={`${testIdPrefix}-${i}-desc`} />
          </div>
          <div>
            <FieldLabel>{t("marketing.budget")}</FieldLabel>
            <TextInput type="number" value={a.budget} onChange={(x) => onUpdate(i, "budget", parseFloat(x) || 0)} testId={`${testIdPrefix}-${i}-budget`} />
          </div>
          <button
            onClick={() => onRemove(i)}
            className="text-[#8A8886] hover:text-[#9E2A2B] pb-3"
            data-testid={`${testIdPrefix}-${i}-remove`}
          >
            <Trash2 className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>
      ))}
      <div className="text-xs text-[#5C5B5A] font-mono pt-2" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
        {lang === "es" ? "Σ presupuesto mensual" : "Σ monthly budget"}: <span className="text-[#1A1A1A]">€{totalBudget.toLocaleString()}</span>
      </div>
    </div>
  );
}
